import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Lock, Mail, AlertCircle, Loader2, ArrowLeft } from "lucide-react";
import { adminLogin, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function AdminLoginPage() {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    if (user && user !== false) return <Navigate to="/admin" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        try {
            const res = await adminLogin(email, password);
            setUser(res.user);
            navigate("/admin", { replace: true });
        } catch (err) {
            setError(formatApiError(err, "Errore di autenticazione"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-navy flex flex-col" data-testid="admin-login-page">
            <header className="p-6 lg:p-10">
                <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-gold text-sm">
                    <ArrowLeft size={16} /> Torna al sito
                </Link>
            </header>

            <main className="flex-1 flex items-center justify-center px-6 pb-20">
                <div className="w-full max-w-md">
                    <div className="text-center mb-10">
                        <div className="inline-block mb-6">
                            <div className="w-14 h-14 border border-gold flex items-center justify-center mx-auto bg-navy">
                                <span className="font-serif text-gold text-2xl">MC</span>
                            </div>
                        </div>
                        <div className="label-eyebrow mb-3">Area Riservata</div>
                        <h1 className="font-serif text-white text-4xl md:text-5xl leading-tight">
                            Accesso <span className="italic text-gold">Avvocato</span>
                        </h1>
                        <p className="text-slate-400 text-sm mt-4">
                            Pannello di gestione articoli e contatti dello Studio.
                        </p>
                    </div>

                    <form onSubmit={onSubmit} data-testid="admin-login-form" className="bg-navy-soft/40 border border-white/10 p-8 space-y-5">
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.25em] font-bold text-gold mb-2" htmlFor="email">Email</label>
                            <div className="relative">
                                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                                <input
                                    id="email"
                                    type="email"
                                    required
                                    autoComplete="username"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="admin-login-email"
                                    className="w-full bg-navy border border-white/15 text-white pl-11 pr-4 py-3.5 placeholder:text-slate-500 focus:outline-none focus:border-gold transition-colors"
                                    placeholder="email@dominio.it"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.25em] font-bold text-gold mb-2" htmlFor="password">Password</label>
                            <div className="relative">
                                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                                <input
                                    id="password"
                                    type="password"
                                    required
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="admin-login-password"
                                    className="w-full bg-navy border border-white/15 text-white pl-11 pr-4 py-3.5 placeholder:text-slate-500 focus:outline-none focus:border-gold transition-colors"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="p-3 border border-red-500/50 bg-red-900/20 flex items-start gap-2 text-red-300 text-sm" data-testid="admin-login-error">
                                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                                <span>{error}</span>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="admin-login-submit"
                            className="btn-gold w-full"
                        >
                            {loading ? (
                                <><Loader2 size={16} className="animate-spin" /> Accesso in corso...</>
                            ) : "Accedi"}
                        </button>
                    </form>
                </div>
            </main>
        </div>
    );
}
